/* eslint-disable react/jsx-props-no-multi-spaces */
import React, { useState, useEffect } from "react";
import { DatePicker, Form } from "antd";
import moment from "moment";
import { exceptDaysActions } from "./store/actions";
import { calendarActions } from "../../containers/Calendar/store/actions";
import { useSelector, useDispatch } from "react-redux";
import "./styles.css";
import i18n from "./localization";
import Notifications from "../../helpers/Notifications";

const format = "DD.MM.YYYY";

/* itemName - имя Form.Item
   label -  label Form.Item
   numDays - число доступных рабочих дней, если null то считается по maxDaysCount
   startDate - дата начала доступного диапазона рабочих дней
   showStartDate - для отображения startDate в пикере 
   initialValue - начальное значение поля
*/
const WorkDayPicker = ({
  form,
  itemName,
  label,
  numDays = null,
  startDate,
  initialValue = null,
  showStartDate = false,
  need15day = false,
  disableJumpers = false,
  disabled,
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const isRu = useSelector((state) => state.globalReducer.isRu);
  const { exceptDays } = useSelector((state) => state.excepsDaysReducer);
  const { workSchedule, specificWorkSchedule } = useSelector((state) => state.calendarReducer);
  const [workDays, setWorkDays] = useState([]);
  const [noActiveWorkDays, setNoActiveWorkDays] = useState(false);
  const [calendarCalculated, setCalendarCalculated] = useState(false)
  const maxDaysCount =  moment().add(10, 'years').diff(moment(), 'days')
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  useEffect(() => {
    if(!exceptDays)
      dispatch(exceptDaysActions.getExcepdDays());
    if(!workSchedule)
      dispatch(calendarActions.getWorkSchedule())
    if(!specificWorkSchedule)
      dispatch(calendarActions.getSpecificWorkSchedule())
    return() => {
      setCalendarCalculated(false)
      dispatch(exceptDaysActions.clearExceptDays())
      dispatch(calendarActions.clearWorkShedule())
      dispatch(calendarActions.clearSpecificWorkSchedule())
    }
  }, [])

  useEffect(() => {
    if (exceptDays && workSchedule && specificWorkSchedule && startDate) {
      const workDaysList = getWorkDaysList(startDate, numDays)
      if(workDaysList?.length > 0){
        setWorkDays(workDaysList);
      }
    }
  }, [exceptDays, workSchedule, specificWorkSchedule, startDate]);

  useEffect(() => {
    if(calendarCalculated){
      if(workDays.length > 0) {
        if (showStartDate)
          form.setFieldsValue({
            [itemName]: need15day 
            ? calculateDate(startDate, 15)  
            : startDate,
          });
        setNoActiveWorkDays(false)
      } else {
        setNoActiveWorkDays(true)
        Notifications.errorNotice(i18n.noActiveWorkDays[isRu], null)
      }
    }
  }, [workDays, startDate, calendarCalculated]);

  const checkExceptDay = (date) => {
    return exceptDays?.find((day) => 
      moment(day.specificDate).format(format) === date.format(format)
    )
  };

  const getWorkDaysList = (startDate, numDays) => {
    const tempDays = [];
    const date = moment(startDate);
    let i = 0;
    let attempts = 0; // Предохранитель от бесконечного цикла
    while (numDays === null || i <= numDays) {
      if (attempts > maxDaysCount) {
        break;
      }

      if (i === numDays && !checkDay(date)) {
        while (!checkDay(date)) {
          date.add(1, "days");
          attempts++;
          if (attempts > maxDaysCount)
            break;
        }
        if (moment().add(10, 'years') > date)
          tempDays.push(date.format(format));
        break;
      }     
      if (checkDay(date)) {
        tempDays.push(date.format(format));
      }
      date.add(1, "days");
      attempts++;
      i++;
    }
    setCalendarCalculated(true)
    return tempDays;
  };

  const checkIsWeekendInWorkShedule = (date) => {
    const hasSpecificWorkSchedule = specificWorkSchedule?.some((value) => 
      moment(value.specificDate).format(format) === date.format(format)
    )
    if(hasSpecificWorkSchedule){
      return false
    }
    const schedule = workSchedule || {};
    const daysMap = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const dayName = daysMap[date.day()];

    return !(schedule[`${dayName}From`] && schedule[`${dayName}To`]);
  };

  const checkDay = (date) => {
    if (numDays === null && date.isBefore(moment(), "day") )
      return false
    const exceptDay = checkExceptDay(date);
    if ((checkIsWeekendInWorkShedule(date) && exceptDay) || (!checkIsWeekendInWorkShedule(date) && exceptDay)) // выходной в расписании и исключение в календаре или не выходной в расписании и исключение в календаре
      return !exceptDay.isDayOff;
    if (checkIsWeekendInWorkShedule(date) && !exceptDay) // выходной в расписании и не исключение в календаре
      return false;
    if (!checkIsWeekendInWorkShedule(date) && !exceptDay) // не выходной в расписании и не исключение в календаре
      return true;
  };

  function disabledDate(current) {
    return !workDays.includes(current.format(format));
  }

  function calculateDate(startDate, interval) {
    const defDay = moment(startDate).add(interval, "days");
    while (!checkDay(defDay)) {
      defDay.add(1, "days");
      if (defDay.diff(moment(), "days") > maxDaysCount) 
        return null;
    }
    return defDay.startOf("day").add(1, "days").subtract(1, "milliseconds");
  }

  function buttonHandler(interval) {
    form.setFieldsValue({ [itemName]: calculateDate(startDate, interval) });
    setIsDatePickerOpen(false);
  }

  return (
    <>
      {exceptDays && workSchedule &&
        <Form.Item
          label={label}
          name={itemName}
          initialValue={initialValue}
          rules={[{
              required: true,
              message: noActiveWorkDays ? i18n.noActiveWorkDays[isRu] : i18n.dateRequired[isRu],
            }]}
          {...otherProps}
        >
          <DatePicker
            disabled={disabled}
            disabledDate={disabledDate}
            format={format}
            open={isDatePickerOpen}
            onOpenChange={(open) => setIsDatePickerOpen(open)} 
            renderExtraFooter={
              disableJumpers || noActiveWorkDays
                ? false
                : () => (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-around",
                        color: "#1890ff",
                      }}
                    >
                      <button className="picker-btn" onClick={() => buttonHandler(5)}>
                        {i18n.days[isRu].format(5)}
                      </button>
                      <button className="picker-btn" onClick={() => buttonHandler(15)}>
                        {i18n.days[isRu].format(15)}
                      </button>
                      <button className="picker-btn" onClick={() => buttonHandler(31)}>
                        {i18n.day[isRu].format(31)}
                      </button>
                    </div>
                  )
            }
          />
        </Form.Item>
      }
    </>
  );
};

export default WorkDayPicker;
